import { newslettersPage } from "common/lib/router"
import { useCsrfToken } from "common/hooks/data/useCsrfToken"
import { useCallback, useState } from "react"
import { trackNewsletterSubscribed as trackNewsletterSubscribedInHouse } from "common/lib/tcEventCollector"
import { useViewer } from "common/hooks/data/useViewer"
import {
  identifyBrazeUser,
  setBrazeTrackingCookie,
  trackNewsletterSubscribed as trackNewsletterSubscribedBraze,
} from "@theconversation/shared-components/braze-tc"
import getConfig from "next/config"

export interface SubscribeEmailParams {
  regionCode: string
  email: string
  recaptchaToken?: string
  newsletterListIds: Set<string>
  action: "subscribe" | "unsubscribe"
}

const DEFAULT_LOCATION = "newsletters-subscription-page-redesign"

interface SuccessResponseFormat {
  success: boolean
}
export interface ErrorResponseFormat {
  errors: string[]
}

/**
 * Hook to toggle an email subscription to newsletters given a region code, email and newsletter list ids.
 */
export const useToggleSubscription = () => {
  const { token: CSRFToken } = useCsrfToken()
  const { isSignedIn } = useViewer()
  const [isLoading, setIsLoading] = useState(false)

  const toggleSubscription = useCallback(
    async ({
      regionCode,
      email,
      recaptchaToken,
      newsletterListIds,
      action,
    }: SubscribeEmailParams) => {
      setIsLoading(true)

      const method = action === "subscribe" ? "POST" : "DELETE"
      const path = newslettersPage.subscriptionsPath({ regionCode, format: "json" })
      const response = await fetch(path, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": CSRFToken,
        },
        body: JSON.stringify({
          subscribe: {
            email,
            location: DEFAULT_LOCATION,
            recaptcha_token: recaptchaToken,
            newsletter_list_ids: Array.from(newsletterListIds),
          },
        }),
      })

      setIsLoading(false)

      if (!response.ok) {
        const errorResponse: ErrorResponseFormat = await response.json()
        return Promise.reject(errorResponse)
      }

      const successResponse: SuccessResponseFormat = await response.json()

      if (action === "subscribe") {
        // Identify the current user with Braze
        identifyBrazeUser(email)

        // Indicate this user should continue to be tracked by Braze in the future
        setBrazeTrackingCookie(getConfig().publicRuntimeConfig.TC_COOKIE_DOMAIN)

        // Track successful subscriptions for each newsletter ID
        newsletterListIds.forEach((newsletterId) => {
          trackNewsletterSubscribedInHouse(newsletterId, isSignedIn) // in-house analytics
          trackNewsletterSubscribedBraze(newsletterId) // Braze events
        })
      }

      return successResponse
    },
    [CSRFToken, isSignedIn],
  )

  return { isLoading, toggleSubscription }
}
